// Playback and deletion responsibility are separate from the broadcast controller
// and its durable Start ledger. No capture/publisher capability is accepted here.
export function createOwnerCameraArchive({ api, isCurrent = () => true, urls = URL }) {
  let disposed = false, visible = false, revision = 0, pageRequest = null, mediaRequest = null;
  const deletes = new Map(), listeners = new Set();
  let state = { open: false, sessions: [], loaded: false, nextCursor: null, loading: false,
    error: null, selectedId: null, parts: [], mediaStatus: 'idle', url: null, partName: null, deletions: {} };
  const current = () => !disposed && isCurrent();
  const emit = patch => { state = { ...state, ...patch }; if (current()) listeners.forEach(fn => fn(state)); };
  function release() {
    revision++;
    mediaRequest?.abort(); mediaRequest = null;
    const url = state.url;
    state = { ...state, url: null, parts: [], partName: null, mediaStatus: 'idle' };
    if (url) urls.revokeObjectURL(url);
  }
  function close() {
    visible = false; pageRequest?.abort(); pageRequest = null; release();
    // A cancelled write has unknown outcome, never fabricated success. Keep the
    // exact ID retry obligation above the panel lifetime, including background.
    const outcomes = { ...state.deletions };
    for (const [id, request] of deletes) { outcomes[id] = 'unknown'; request.abort(); }
    deletes.clear();
    emit({ open: false, loading: false, selectedId: null, deletions: outcomes });
  }
  async function load(more = false) {
    if (!current() || !visible || pageRequest || (more && !state.nextCursor)) return;
    const request = new AbortController(); pageRequest = request;
    const cursor = more ? state.nextCursor : null;
    emit({ loading: true, error: null });
    try {
      const page = await api.list(cursor, request.signal);
      if (!current() || !visible || pageRequest !== request || request.signal.aborted) return;
      const rows = new Map(state.sessions.filter(s => more || state.deletions[s.id]).map(s => [s.id, s]));
      page.sessions.forEach(s => rows.set(s.id, s));
      if (!more && state.selectedId && !rows.has(state.selectedId)) { release(); emit({ selectedId: null }); }
      emit({ sessions: [...rows.values()], nextCursor: page.nextCursor, loaded: true });
    } catch {
      if (current() && visible && pageRequest === request) emit({ error: 'Recordings could not be loaded. Retry.', retryMore: more });
    } finally {
      if (pageRequest === request) { pageRequest = null; emit({ loading: false }); }
    }
  }
  async function select(id) {
    if (!current() || !visible || state.deletions[id]) return;
    const row = state.sessions.find(s => s.id === id && s.ownerId === api.ownerId);
    if (!row) return;
    release();
    emit({ selectedId: id, mediaStatus: row.state === 'ended' && row.recording.state === 'complete' ? 'loading' : 'unavailable' });
    if (state.mediaStatus !== 'loading') return;
    const request = new AbortController(); mediaRequest = request; const token = revision;
    try {
      const parts = await api.parts(id, request.signal);
      if (current() && visible && token === revision && !request.signal.aborted) emit({ parts, mediaStatus: parts.length ? 'choose' : 'unavailable' });
    } catch {
      if (current() && visible && token === revision) emit({ mediaStatus: 'error' });
    }
  }
  async function play(name) {
    const id = state.selectedId, part = state.parts.find(p => p.name === name);
    if (!current() || !visible || !part || state.deletions[id]) return;
    const parts = state.parts;
    release(); emit({ parts, partName: name, mediaStatus: 'loading' });
    const token = revision, request = new AbortController(); mediaRequest = request;
    const allowed = () => current() && visible && token === revision && !request.signal.aborted && !state.deletions[id];
    try {
      const blob = await api.media(id, part, request.signal);
      if (!allowed()) return;
      api.assertCurrent();
      const url = urls.createObjectURL(blob);
      // Creation can reenter disposal; never strand or publish the new URL.
      if (!allowed()) { urls.revokeObjectURL(url); return; }
      emit({ url, mediaStatus: 'ready' });
    } catch {
      if (allowed()) emit({ mediaStatus: 'error' });
    }
  }
  async function remove(id) {
    if (!current() || !visible || deletes.has(id)) return;
    const row = state.sessions.find(s => s.id === id && s.ownerId === api.ownerId);
    if (!row || !['ended', 'failed'].includes(row.state) || state.deletions[id] === 'complete') return;
    if (state.selectedId === id) { release(); emit({ selectedId: null }); }
    const request = new AbortController(); deletes.set(id, request);
    emit({ deletions: { ...state.deletions, [id]: 'sending' } });
    try {
      const outcome = await api.remove(id, request.signal);
      if (current() && visible && deletes.get(id) === request && !request.signal.aborted) {
        emit({ deletions: { ...state.deletions, [id]: outcome } });
      }
    } catch {
      if (current() && visible && deletes.get(id) === request) emit({ deletions: { ...state.deletions, [id]: 'unknown' } });
    } finally { if (deletes.get(id) === request) deletes.delete(id); }
  }
  return { getState: () => state, subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    open() { if (!current()) return; visible = true; emit({ open: true }); if (!state.loaded) void load(); },
    close, load, select, play, remove,
    playbackError() { const id = state.selectedId; release(); emit({ selectedId: id, mediaStatus: 'error' }); },
    dispose() { disposed = true; close(); listeners.clear(); },
  };
}
